//services
import { validateStep3, createAddDigitalBalance, getBalancesByCompanyId } from 'services/balance.services';

//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import {
	getAltshulerBankAccount,
	getCompanyBankAccount,
	getTranfserAmount,
	isLoading,
	setAltshulerBankAccount,
	getErrorPopup,
	hideErrorPopup,
	showErrorPopup,
	getShowMobilePopup,
	getShowComputerPopup,
	hideMobilePopup,
	hideComputerPopup,
	resetAddBalanceProcess,
	getMobileRetryCount,
	incrementMobileRetryCount,
} from 'state/reducers/balance.reducers';
import { getExistingBankAccounts } from 'state/reducers/bank-account.reducers';

//components
import TransferDetailsForm from './TransferDetailsForm';
import ImagePopup from 'components/ImagePopup';
//constants
import { invalidFields } from 'components/FormCreator/types';

import { Fragment, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
import { balanceSteps } from 'constants/balance.constants';
import { getMobile } from 'state/reducers/ui.reducers';
import { useNavigate } from 'react-router-dom';
import { selectImageUrl } from 'state/reducers/translation.reducers';

const MAX_RETRY_ATTEMPTS = 3;
const MAX_RETRY_RESEND_SMS = 3;

export default function Step3(props: { handleCancel: any; transferProofFile: File | null; handleBack: any }) {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const imageUrl = useAppSelector(selectImageUrl);

	const companyBankAccountId = useAppSelector(getCompanyBankAccount);
	const altshulerBankAccountId = useAppSelector(getAltshulerBankAccount);
	const transferAmount = useAppSelector(getTranfserAmount);
	const isMobile = useAppSelector(getMobile);
	const existingBankAccounts = useAppSelector(getExistingBankAccounts);
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});
	const loading: boolean = useAppSelector(isLoading);
	const retryCount = useAppSelector(getMobileRetryCount);
	const errorPopup = useAppSelector(getErrorPopup);
	const isMobilePopupOpen = useAppSelector(getShowMobilePopup);
	const isComputerPopupOpen = useAppSelector(getShowComputerPopup);

	// Get selected bank account details
	const selectedBankAccount = useMemo(() => {
		return existingBankAccounts.find((account) => account.id === companyBankAccountId);
	}, [existingBankAccounts, companyBankAccountId]);

	const isContinueDisabled = useMemo(() => {
		return loading || !!_.keys(invalidFields).length;
	}, [loading, invalidFields]);

	const handleBankListPick = (accountId: number) => {
		dispatch(setAltshulerBankAccount(accountId));
	};

	const handleMobile = async () => {
		await handleDigitalBalanceRequest(true);
	};

	const handleContinueOnComputer = async () => {
		await handleDigitalBalanceRequest(false);
	};

	const handleDigitalBalanceRequest = async (toContinueOnMobile: boolean) => {
		if (!selectedBankAccount || !transferAmount) {
			return;
		}

		const userBankAccountDetails = {
			bankId: selectedBankAccount.bankId || selectedBankAccount.bank?.id,
			branchNumber: selectedBankAccount.bankBranch,
			accountNumber: selectedBankAccount.accountNumber,
		};

		const amount = parseFloat(transferAmount);
		const currencyId = selectedBankAccount.currencyId;

		try {
			// Service will handle opening popups via Redux
			await dispatch(createAddDigitalBalance(toContinueOnMobile, userBankAccountDetails, amount, currencyId));
		} catch (error) {
			// Show error popup
			dispatch(
				showErrorPopup({
					error: error,
					retryData: {
						toContinueOnMobile,
						userBankAccountDetails,
						amount,
						currencyId,
					},
				})
			);
		}
	};

	const handleCompleteMobileFlow = () => {
		// Close mobile popup, reset form state, and navigate to homepage
		dispatch(hideMobilePopup());
		dispatch(resetAddBalanceProcess());
		dispatch(getBalancesByCompanyId());
		navigate('/dashboard');
	};

	const handleRetryMobile = async () => {
		if (retryCount >= MAX_RETRY_RESEND_SMS) {
			return;
		}

		dispatch(incrementMobileRetryCount());

		// Close mobile popup and retry the mobile API call
		dispatch(hideMobilePopup());
		await handleMobile();
	};

	const handleReturnToHomepage = () => {
		// Close computer popup, reset form state, and navigate to home page
		dispatch(hideComputerPopup());
		dispatch(resetAddBalanceProcess());
		navigate('/dashboard');
	};

	const handleBackButton = () => {
		props.handleBack(balanceSteps.Currency);
	};

	const handleTryAgain = async () => {
		// Check if retries are available
		if (retryCount >= MAX_RETRY_ATTEMPTS) {
			return;
		}

		// Increment retry count
		dispatch(incrementMobileRetryCount());

		// Hide error popup
		dispatch(hideErrorPopup());

		if (errorPopup.retryData) {
			const { toContinueOnMobile, userBankAccountDetails, amount, currencyId } = errorPopup.retryData;

			try {
				await dispatch(createAddDigitalBalance(toContinueOnMobile, userBankAccountDetails, amount, currencyId));
			} catch (error) {
				// Show error popup again if retry fails
				dispatch(
					showErrorPopup({
						error: error,
						retryData: errorPopup.retryData,
					})
				);
			}
		}
	};

	const handleGoToHomepage = () => {
		dispatch(hideErrorPopup());
		dispatch(resetAddBalanceProcess());
		navigate('/dashboard');
	};

	return (
		<Fragment>
			<TransferDetailsForm
				handleBankListPick={handleBankListPick}
				invalidFields={invalidFields}
				handleMobile={handleMobile}
				handleContinueOnComputer={handleContinueOnComputer}
				handleBack={handleBackButton}
				handleCancel={props.handleCancel}
				loading={loading}
				isContinueDisabled={isContinueDisabled}
				isMobile={isMobile}
			/>

			{/* TODO: after mobile functionality is implemented remove the comment */}
			{/* Mobile Popup */}
			{/* <ImagePopup
				open={isMobilePopupOpen}
				image="/static_images/success-clipboard-icon.png"
				title={t('page.balance.add.digital.step.3.popup_translations.mobile_popup.title')}
				buttons={[
					{
						label: t(
							'page.balance.add.digital.step.3.popup_translations.mobile_popup.completed_button.text'
						),
						onClick: handleCompleteMobileFlow,
						variant: 'contained',
						autoFocus: true,
					},
					//TODO: after resend functionality is implemented remove the comment
					// {
					// 	label: t(
					// 		'page.balance.add.digital.step.3.popup_translations.mobile_popup.retry_sms_button.text'
					// 	),
					// 	onClick: handleRetryMobile,
					// 	variant: 'outlined',
					// 	disabled: retryCount >= MAX_RETRY_RESEND_SMS,
					// },
				]}
			/> */}

			{/* Computer Popup */}
			<ImagePopup
				open={isComputerPopupOpen}
				image="/static_images/success-clipboard-icon.png"
				title={t('page.balance.add.digital.step.3.popup_translations.computer_popup.title')}
				buttons={[
					{
						label: t(
							'page.balance.add.digital.step.3.popup_translations.computer_popup.completed_button.text'
						),
						onClick: handleReturnToHomepage,
						variant: 'contained',
						autoFocus: true,
					},
				]}
			/>

			{/* Error Popup */}
			<ImagePopup
				open={errorPopup.open}
				image={'/static_images/error-clipboard-icon.png'}
				title={t('page.balance.add.digital.step.3.popup_translations.error_popup.title')}
				buttons={[
					{
						label: t(
							'page.balance.add.digital.step.3.popup_translations.error_popup.try_again_button.text'
						),
						onClick: handleTryAgain,
						variant: 'contained',
						autoFocus: true,
						disabled: retryCount >= MAX_RETRY_ATTEMPTS,
					},
					{
						label: t('page.balance.add.digital.step.3.popup_translations.error_popup.homepage_button.text'),
						onClick: handleGoToHomepage,
						variant: 'outlined',
					},
				]}
			/>
		</Fragment>
	);
}
